import { useEffect, useState } from "react";
import { Check, X, Eye, RefreshCw, Banknote } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatRupiah, formatDate } from "@/lib/dummyData";
import { AdminAPI } from "@/lib/api";
import { toast } from "sonner";

// Halaman verifikasi pembayaran manual (peran Admin Keanggotaan & Keuangan).
// Menampilkan bukti transfer yang diunggah user, lalu admin menyetujui / menolak.
const statusStyle = {
  PENDING: "bg-amber-warm/20 text-amber-700",
  APPROVED: "bg-sage/20 text-forest",
  REJECTED: "bg-terracotta/15 text-terracotta",
};

// Base URL server untuk menampilkan gambar bukti (proof_url berupa path /uploads/...).
const FILE_BASE = (process.env.REACT_APP_API_URL || "http://localhost:4000/api").replace(/\/api\/?$/, "");

export default function AdminPayments() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("PENDING");
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null); // URL bukti yang sedang dilihat

  // Muat daftar konfirmasi pembayaran sesuai filter status.
  const load = async () => {
    setLoading(true);
    try {
      setRows(await AdminAPI.paymentConfirmations(filter === "SEMUA" ? undefined : filter));
    } catch {
      toast.error("Gagal memuat data (pastikan login sebagai admin keuangan)");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  // Setujui pembayaran → backend menandai pesanan PAID / menambah saldo top-up + mencatat ledger.
  const approve = async (id) => {
    try {
      await AdminAPI.approvePayment(id, "Bukti valid");
      toast.success("Pembayaran disetujui");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal menyetujui");
    }
  };

  // Tolak pembayaran (bukti tidak valid).
  const reject = async (id) => {
    try {
      await AdminAPI.rejectPayment(id, "Bukti tidak valid / tidak sesuai");
      toast.success("Pembayaran ditolak");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal menolak");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight">Verifikasi Pembayaran</h1>
          <p className="text-ink/75 mt-2 text-sm">Konfirmasi manual bukti transfer pesanan & top-up (tanpa payment gateway).</p>
        </div>
        <Button variant="outline" className="rounded-lg border-stone-soft" onClick={load}><RefreshCw size={15} /> Muat Ulang</Button>
      </div>

      {/* Filter status */}
      <div className="flex gap-2 overflow-x-auto">
        {["PENDING", "APPROVED", "REJECTED", "SEMUA"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap ${filter === s ? "bg-forest text-white" : "bg-cream text-ink/75 hover:bg-stone-soft/30"}`}
          >
            {s}
          </button>
        ))}
      </div>

      <Card className="rounded-xl border-stone-soft p-5">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-stone-soft bg-cream/40 hover:bg-cream/40">
                <TableHead className="text-[10px] uppercase tracking-wider">Pengirim</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Tujuan</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Jumlah</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Bank</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Tanggal</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Bukti</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center text-ink/55 py-10">Memuat…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center text-ink/55 py-10">Tidak ada data untuk status ini.</TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id} className="border-stone-soft hover:bg-cream/30" data-testid={`payment-row-${r.id}`}>
                  <TableCell>
                    <div className="font-semibold text-sm text-ink">{r.sender_name || r.user_name}</div>
                    <div className="text-[11px] text-ink/55">{r.user_name}</div>
                  </TableCell>
                  <TableCell>
                    <Badge className="bg-forest/10 text-forest border-0 rounded-full text-[10px] uppercase tracking-wider">
                      {r.purpose === "ORDER" ? `Pesanan ${r.order_number || ""}`
                        : r.purpose === "MEMBERSHIP" ? "Pendaftaran Anggota"
                        : r.purpose === "SAVINGS_WAJIB" ? "Setoran Simpanan Wajib"
                        : r.purpose === "SAVINGS_SUKARELA" ? "Setoran Simpanan Sukarela"
                        : "Top-up Dompet"}
                    </Badge>
                    {r.purpose === "MEMBERSHIP" && r.note && <div className="text-[11px] text-ink/55 mt-1">{r.note}</div>}
                  </TableCell>
                  <TableCell className="text-right font-display font-semibold text-ink">{formatRupiah(r.amount)}</TableCell>
                  <TableCell className="text-sm text-ink/75">{r.bank_name || "-"}</TableCell>
                  <TableCell className="text-sm text-ink/75">{formatDate(r.created_at)}</TableCell>
                  <TableCell>
                    {r.bank_name === "MIDTRANS" ? (
                      // Pendaftaran dibayar lewat Midtrans — tanpa bukti transfer; status dari Midtrans.
                      Number(r.midtrans_paid) ? (
                        <span className="text-xs font-semibold text-forest">Lunas via Midtrans</span>
                      ) : (
                        <span className="text-xs text-amber-700">Belum dibayar</span>
                      )
                    ) : r.proof_url ? (
                      <button onClick={() => setPreview(FILE_BASE + r.proof_url)} className="text-forest hover:underline inline-flex items-center gap-1 text-xs">
                        <Eye size={13} /> Lihat
                      </button>
                    ) : <span className="text-ink/40 text-xs">—</span>}
                  </TableCell>
                  <TableCell>
                    <Badge className={`${statusStyle[r.status]} border-0 rounded-full text-[10px] uppercase tracking-wider`}>{r.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {r.status === "PENDING" ? (
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" disabled={r.bank_name === "MIDTRANS" && !Number(r.midtrans_paid)} className="rounded-lg bg-forest hover:bg-forest-dark text-white h-8" onClick={() => approve(r.id)} data-testid={`payment-approve-${r.id}`}>
                          <Check size={14} /> Setujui
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-lg border-terracotta/40 text-terracotta hover:bg-terracotta/10 h-8" onClick={() => reject(r.id)} data-testid={`payment-reject-${r.id}`}>
                          <X size={14} />
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-ink/45">{r.note || "—"}</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Modal sederhana untuk melihat bukti transfer */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-ink/60 grid place-items-center p-6" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-2xl p-4 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 font-semibold text-ink"><Banknote size={16} className="text-forest" /> Bukti Transfer</div>
              <button onClick={() => setPreview(null)} className="text-ink/55 hover:text-ink"><X size={18} /></button>
            </div>
            <img src={preview} alt="Bukti transfer" className="w-full rounded-lg border border-stone-soft" />
          </div>
        </div>
      )}

      {/* Pembayaran gerai ke koperasi (uang masuk kas) */}
      <SettlementsPanel onPreview={setPreview} />

      {/* Penarikan simpanan sukarela anggota (verifikasi + konfirmasi transfer) */}
      <SavingsWithdrawalsPanel />

      {/* Penarikan dana penjual & persetujuan toko */}
      <WithdrawalsPanel />
      <ShopsPanel />
    </div>
  );
}

// ===== Penarikan Simpanan Sukarela Anggota =====
// Dua tahap oleh Admin Keuangan: (1) Verifikasi pengajuan, (2) Konfirmasi transfer →
// saat konfirmasi saldo sukarela anggota dipotong & kas koperasi DEBIT.
const SW_STATUS_STYLE = {
  PENDING: "bg-amber-warm/20 text-amber-700",
  APPROVED: "bg-sky-100 text-sky-700",
  PAID: "bg-sage/20 text-forest",
  REJECTED: "bg-terracotta/15 text-terracotta",
};

function SavingsWithdrawalsPanel() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("PENDING");
  const load = () => AdminAPI.savingsWithdrawals(filter === "SEMUA" ? undefined : filter).then(setRows).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch (err) { toast.error(err?.response?.data?.error || "Gagal"); }
  };

  return (
    <Card className="rounded-xl border-stone-soft p-5">
      <h3 className="font-display font-semibold text-ink mb-1">Penarikan Simpanan Sukarela</h3>
      <p className="text-xs text-ink/55 mb-3">
        Verifikasi pengajuan anggota, lalu konfirmasi setelah dana ditransfer — saldo sukarela dipotong saat konfirmasi.
      </p>
      <div className="flex gap-2 mb-3">
        {["PENDING", "APPROVED", "PAID", "REJECTED", "SEMUA"].map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${filter === s ? "bg-forest text-white" : "bg-cream text-ink/75 hover:bg-stone-soft/30"}`}>
            {s}
          </button>
        ))}
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-stone-soft bg-cream/40 hover:bg-cream/40">
              <TableHead className="text-[10px] uppercase tracking-wider">Anggota</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-right">Nominal</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-right">Saldo Sukarela</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Rekening Tujuan</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Tanggal</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Status</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-ink/55 py-8">Tidak ada pengajuan untuk status ini.</TableCell></TableRow>
            ) : rows.map((w) => (
              <TableRow key={w.id} className="border-stone-soft" data-testid={`savings-wd-row-${w.id}`}>
                <TableCell>
                  <div className="font-semibold text-sm text-ink">{w.user_name}</div>
                  <div className="text-[11px] text-ink/55">{w.koperasi_member_number}</div>
                </TableCell>
                <TableCell className="text-right font-display font-semibold text-ink">{formatRupiah(Number(w.amount))}</TableCell>
                <TableCell className="text-right text-sm text-ink/75">{formatRupiah(Number(w.saldo_sukarela || 0))}</TableCell>
                <TableCell className="text-sm text-ink/75">
                  {w.bank_name} {w.bank_account_number}
                  <div className="text-[11px] text-ink/55">a.n. {w.account_holder}</div>
                </TableCell>
                <TableCell className="text-sm text-ink/75">{formatDate(w.created_at)}</TableCell>
                <TableCell>
                  <Badge className={`${SW_STATUS_STYLE[w.status]} border-0 rounded-full text-[10px] uppercase tracking-wider`}>{w.status}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  {w.status === "PENDING" ? (
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" className="rounded-lg bg-forest hover:bg-forest-dark text-white h-8"
                        onClick={() => act(() => AdminAPI.approveSavingsWithdrawal(w.id, "Pengajuan terverifikasi"), "Pengajuan diverifikasi")}
                        data-testid={`savings-wd-approve-${w.id}`}>
                        <Check size={14} /> Verifikasi
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-lg border-terracotta/40 text-terracotta hover:bg-terracotta/10 h-8"
                        onClick={() => act(() => AdminAPI.rejectSavingsWithdrawal(w.id, "Pengajuan ditolak"), "Pengajuan ditolak")}>
                        <X size={14} />
                      </Button>
                    </div>
                  ) : w.status === "APPROVED" ? (
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" className="rounded-lg bg-forest hover:bg-forest-dark text-white h-8"
                        onClick={() => act(() => AdminAPI.confirmSavingsWithdrawal(w.id), "Penarikan dikonfirmasi — saldo anggota dipotong")}
                        data-testid={`savings-wd-confirm-${w.id}`}>
                        <Check size={14} /> Konfirmasi Transfer
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-lg border-terracotta/40 text-terracotta hover:bg-terracotta/10 h-8"
                        onClick={() => act(() => AdminAPI.rejectSavingsWithdrawal(w.id, "Dibatalkan saat konfirmasi"), "Pengajuan ditolak")}>
                        <X size={14} />
                      </Button>
                    </div>
                  ) : (
                    <span className="text-xs text-ink/45">{w.note || "—"}</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

// ===== Pelunasan Barang Gerai → Koperasi (uang masuk kas) =====
function SettlementsPanel({ onPreview }) {
  const [rows, setRows] = useState([]);
  const load = () => AdminAPI.goodsSettlements("PENDING").then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);
  const act = async (id, fn, msg) => {
    try { await fn(id); toast.success(msg); load(); }
    catch (err) { toast.error(err?.response?.data?.error || "Gagal"); }
  };
  return (
    <Card className="rounded-xl border-stone-soft p-5">
      <h3 className="font-display font-semibold text-ink mb-1">Pelunasan Barang Gerai (menunggu verifikasi)</h3>
      <p className="text-xs text-ink/55 mb-3">Pembayaran gerai atas barang yang diterima. Menyetujui = uang masuk kas koperasi.</p>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-stone-soft bg-cream/40 hover:bg-cream/40">
              <TableHead className="text-[10px] uppercase tracking-wider">Gerai</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">No. Surat Pesanan</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-right">Jumlah</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Bank</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Bukti</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-ink/55 py-8">Tidak ada pelunasan menunggu.</TableCell></TableRow>
            ) : rows.map((s) => (
              <TableRow key={s.id} className="border-stone-soft" data-testid={`settlement-row-${s.id}`}>
                <TableCell>
                  <div className="font-semibold text-sm text-ink">{s.gerai_org_name || s.gerai_name || "—"}</div>
                  <div className="text-[11px] text-ink/55">{s.gerai_name}</div>
                </TableCell>
                <TableCell className="font-mono text-xs font-semibold text-ink">{s.po_number}</TableCell>
                <TableCell className="text-right font-display font-semibold text-ink">{formatRupiah(Number(s.amount))}</TableCell>
                <TableCell className="text-sm text-ink/75">{s.bank_name || "-"}<div className="text-[11px] text-ink/50">{s.sender_name || ""}</div></TableCell>
                <TableCell>
                  {s.proof_url ? (
                    <button onClick={() => onPreview(FILE_BASE + s.proof_url)} className="text-forest hover:underline inline-flex items-center gap-1 text-xs">
                      <Eye size={13} /> Lihat
                    </button>
                  ) : <span className="text-ink/40 text-xs">—</span>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" className="rounded-lg bg-forest hover:bg-forest-dark text-white h-8" onClick={() => act(s.id, AdminAPI.approveSettlement, "Pelunasan disetujui — kas koperasi bertambah")} data-testid={`settlement-approve-${s.id}`}>
                      <Check size={14} /> Setujui
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-lg border-terracotta/40 text-terracotta hover:bg-terracotta/10 h-8" onClick={() => act(s.id, AdminAPI.rejectSettlement, "Pelunasan ditolak")}>
                      <X size={14} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

// ===== Penarikan Dana Penjual =====
function WithdrawalsPanel() {
  const [rows, setRows] = useState([]);
  const load = () => AdminAPI.withdrawals("PENDING").then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);
  const act = async (id, fn) => {
    try { await fn(id); toast.success("Diproses"); load(); }
    catch (err) { toast.error(err?.response?.data?.error || "Gagal"); }
  };
  return (
    <Card className="rounded-xl border-stone-soft p-5">
      <h3 className="font-display font-semibold text-ink mb-3">Penarikan Dana Penjual (menunggu)</h3>
      <Table>
        <TableHeader>
          <TableRow className="border-stone-soft bg-cream/40 hover:bg-cream/40">
            <TableHead className="text-[10px] uppercase tracking-wider">Penjual</TableHead>
            <TableHead className="text-[10px] uppercase tracking-wider text-right">Jumlah</TableHead>
            <TableHead className="text-[10px] uppercase tracking-wider">Rekening</TableHead>
            <TableHead className="text-[10px] uppercase tracking-wider text-right">Aksi</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow><TableCell colSpan={4} className="text-center text-ink/55 py-8">Tidak ada permintaan.</TableCell></TableRow>
          ) : rows.map((w) => (
            <TableRow key={w.id} className="border-stone-soft" data-testid={`withdrawal-row-${w.id}`}>
              <TableCell className="text-sm font-medium text-ink">{w.user_name}</TableCell>
              <TableCell className="text-right font-display font-semibold">{formatRupiah(w.amount)}</TableCell>
              <TableCell className="text-sm text-ink/70">{w.bank_name} {w.bank_account_number}</TableCell>
              <TableCell className="text-right">
                <div className="flex gap-1 justify-end">
                  <Button size="sm" className="rounded-lg bg-forest hover:bg-forest-dark text-white h-8" onClick={() => act(w.id, AdminAPI.approveWithdrawal)} data-testid={`withdrawal-approve-${w.id}`}><Check size={14} /> Cairkan</Button>
                  <Button size="sm" variant="outline" className="rounded-lg border-terracotta/40 text-terracotta h-8" onClick={() => act(w.id, AdminAPI.rejectWithdrawal)}><X size={14} /></Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

// ===== Persetujuan Toko/Gerai =====
function ShopsPanel() {
  const [rows, setRows] = useState([]);
  const load = () => AdminAPI.shops("PENDING").then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);
  const act = async (id, action) => {
    try { await AdminAPI.shopAction(id, action); toast.success(action === "approve" ? "Toko disetujui" : "Ditolak"); load(); }
    catch (err) { toast.error(err?.response?.data?.error || "Gagal"); }
  };
  return (
    <Card className="rounded-xl border-stone-soft p-5">
      <h3 className="font-display font-semibold text-ink mb-3">Pengajuan Buka Gerai/Toko (menunggu)</h3>
      <Table>
        <TableHeader>
          <TableRow className="border-stone-soft bg-cream/40 hover:bg-cream/40">
            <TableHead className="text-[10px] uppercase tracking-wider">Toko</TableHead>
            <TableHead className="text-[10px] uppercase tracking-wider">Pemilik</TableHead>
            <TableHead className="text-[10px] uppercase tracking-wider">Jenis</TableHead>
            <TableHead className="text-[10px] uppercase tracking-wider text-right">Aksi</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow><TableCell colSpan={4} className="text-center text-ink/55 py-8">Tidak ada pengajuan.</TableCell></TableRow>
          ) : rows.map((s) => (
            <TableRow key={s.id} className="border-stone-soft" data-testid={`shop-row-${s.id}`}>
              <TableCell className="text-sm font-medium text-ink">{s.shop_name}</TableCell>
              <TableCell className="text-sm text-ink/70">{s.owner_name}</TableCell>
              <TableCell><Badge className="bg-stone-soft/40 text-ink/70 border-0 rounded-full text-[10px]">{s.shop_type}</Badge></TableCell>
              <TableCell className="text-right">
                <div className="flex gap-1 justify-end">
                  <Button size="sm" className="rounded-lg bg-forest hover:bg-forest-dark text-white h-8" onClick={() => act(s.id, "approve")} data-testid={`shop-approve-${s.id}`}><Check size={14} /> Setujui</Button>
                  <Button size="sm" variant="outline" className="rounded-lg border-terracotta/40 text-terracotta h-8" onClick={() => act(s.id, "reject")}><X size={14} /></Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
